import { createSelector } from "@reduxjs/toolkit";
import {
  ERoomColoringCategoryId,
  IRoomColoring,
  VALIDATION_DECISION_COLOR_SPECS,
  makeValidationDecisionColorFn,
  RFP_VALIDATION_COLOR_SPECS,
  makeStandardRoomValidationColorFn,
  makeSpecifiedArticlesValidationColorFn,
} from "../RoomColoring";
import { ERoomColoringSubcategoryId } from "../RoomColoring/roomColoringConstants";
import {
  selectHttpGetRoomsCurrentFloorResult,
  selectHttpRoomsBasedOnActiveProjectTab,
} from "./httpRoomSelectors";

/* -------------------------------------------------------------------------- */
/*  Specified Articles → dynamiske fargefilter                                 */
/* -------------------------------------------------------------------------- */

export const selectUniqueSpecifiedArticlesKeys = createSelector(
  [selectHttpRoomsBasedOnActiveProjectTab],
  (roomsHashmap): string[] => {
    const unique = new Set<string>();

    // Always include overall
    unique.add("specifiedArticles.overall");

    Object.values(roomsHashmap).forEach((room) => {
      const perResp = room?.validation?.specifiedArticles?.perResponsibility;

      if (perResp && typeof perResp === "object") {
        Object.keys(perResp).forEach((respKey) => {
          unique.add(`specifiedArticles.${respKey}`);
        });
      }
    });

    return Array.from(unique).sort((a, b) => {
      // Put overall first
      if (a === "specifiedArticles.overall") return -1;
      if (b === "specifiedArticles.overall") return 1;
      return a.localeCompare(b);
    });
  }
);

const titleForSpecifiedArticlesKey = (key: `specifiedArticles.${string}`) => {
  const suffix = key.split(".")[1] ?? "";
  if (suffix === "overall") return "Artikkelvalidering – samlet";
  return `Artikkelvalidering – ${suffix}`;
};

export const selectSpecifiedArticlesValidationColorItems = createSelector(
  [selectUniqueSpecifiedArticlesKeys],
  (keys): IRoomColoring[] => {
    return keys.map((key) => {
      const suffix = key.split(".")[1];

      const trendKeyPath =
        suffix === "overall"
          ? `validation.specifiedArticles.summary.matchRatePct`
          : `validation.specifiedArticles.perResponsibility.${suffix}.summary.matchRatePct`;

      return {
        id: key,
        title: titleForSpecifiedArticlesKey(key as `specifiedArticles.${string}`),

        categoryId: ERoomColoringCategoryId.RfpArtikkelvalidering,
        subcategoryId: ERoomColoringSubcategoryId.ValideringStatus,
        order: suffix === "overall" ? 0 : 10,
        tags: ["rfp", "artikkel", "validering"],

        explanation: {
          shortNo: "Fargelegger rom etter treffprosent for artikkelvalidering (intervaller).",
          longNo:
            "Dette viser hvor stor andel av spesifiserte artikler som matcher i rommet. 'Samlet' er aggregert på tvers av ansvar.",
          docKey: "colorfilters/specified-articles",
        },

        colorSpecs: RFP_VALIDATION_COLOR_SPECS,
        trendKeyPath,
        getPolygonColors: makeSpecifiedArticlesValidationColorFn(
          key as `specifiedArticles.${string}`
        ),
      };
    });
  }
);

/* -------------------------------------------------------------------------- */
/*  Standardrom-validering → dynamiske fargefilter                             */
/* -------------------------------------------------------------------------- */

export const selectUniqueStandardRoomValidationsKeys = createSelector(
  [selectHttpRoomsBasedOnActiveProjectTab],
  (roomsHashmap): string[] => {
    const unique = new Set<string>();

    Object.values(roomsHashmap).forEach((room) => {
      const standardRoom = room?.validation?.standardRoom;
      if (standardRoom && typeof standardRoom === "object") {
        Object.keys(standardRoom).forEach((key) => {
          // "standardRoom.E", "standardRoom.V" osv.
          unique.add(`standardRoom.${key}`);
        });
      }
    });

    return Array.from(unique).sort();
  }
);

// Hjelp: norsk tittel basert på nøkkelen
const titleForStandardRoomKey = (key: `standardRoom.${string}`): string => {
  const disiplin = key.split(".")[1] ?? "";
  return `Standardrom – validering (${disiplin})`;
};

export const selectStandardRoomValidationColorItems = createSelector(
  [selectUniqueStandardRoomValidationsKeys],
  (keys): IRoomColoring[] => {
    return keys.map((key) => {
      const disiplin = key.split(".")[1] ?? "";

      return {
        id: key, // f.eks. "standardRoom.E"
        title: titleForStandardRoomKey(key as `standardRoom.${string}`),

        categoryId: ERoomColoringCategoryId.RfpStandardromValidering,
        subcategoryId: ERoomColoringSubcategoryId.ValideringStatus,
        order: 10,
        tags: ["rfp", "standardrom", "validering", disiplin],

        explanation: {
          shortNo: "Fargelegger rom etter treffprosent for standardromvalidering (intervaller).",
          longNo:
            "Dette viser hvor godt rommet matcher kravene i standardrom for valgt disiplin. Brukes for rask QA i plantegning.",
          docKey: "colorfilters/standardrom-validering",
        },

        colorSpecs: RFP_VALIDATION_COLOR_SPECS,
        trendKeyPath: `validation.standardRoom.${disiplin}.summary.matchRatePct`,
        getPolygonColors: makeStandardRoomValidationColorFn(key as `standardRoom.${string}`),
      };
    });
  }
);

/* -------------------------------------------------------------------------- */
/*  Beslutning (validationDecisions) → dynamiske fargefilter                   */
/* -------------------------------------------------------------------------- */

export const DECISION_FILTER_PREFIX = "decision:" as const;

export type IDecisionFilterId = `${typeof DECISION_FILTER_PREFIX}${string}.${string}`;
// returnerer f.eks. ["decision:standardRoom.E", "decision:standardRoom.V"]

export const selectUniqueValidationDecisionKeys = createSelector(
  [selectHttpGetRoomsCurrentFloorResult],
  (roomsMap): IDecisionFilterId[] => {
    if (!roomsMap) return [];

    const keys = new Set<IDecisionFilterId>();

    for (const room of Object.values(roomsMap)) {
      const std = room.validationDecisions?.standardRoom;
      if (!std) continue;

      for (const discipline of Object.keys(std)) {
        keys.add(`${DECISION_FILTER_PREFIX}standardRoom.${discipline}`);
      }
    }

    return Array.from(keys).sort();
  }
);

const titleForDecisionKey = (key: string) => {
  // "standardRoom.E" -> "Beslutning – Standardrom (E)"
  const [branch, discipline] = key.split(".");
  if (branch === "standardRoom") return `Beslutning – Standardrom (${discipline})`;
  return `Beslutning – ${branch} (${discipline})`;
};

const toDecisionKey = (id: IDecisionFilterId) =>
  id.replace(DECISION_FILTER_PREFIX, "") as `${string}.${string}`;

export const selectValidationDecisionColorItems = createSelector(
  [selectUniqueValidationDecisionKeys],
  (keys): IRoomColoring[] => {
    return keys.map((id) => {
      const decisionKey = toDecisionKey(id);
      const [branch, disiplin] = decisionKey.split(".") as [string, string];

      return {
        id, // "decision:standardRoom.E"
        title: titleForDecisionKey(decisionKey),

        categoryId: ERoomColoringCategoryId.RfpStandardromValidering,
        subcategoryId: ERoomColoringSubcategoryId.ValideringStatus,
        order: 100,
        tags: ["beslutning", "validering", branch, disiplin],

        explanation: {
          shortNo:
            "Fargelegger rom etter gjeldende beslutningsstatus (godkjent / avvik / må sjekkes).",
          longNo:
            "Dette viser manuelt beslutningsutfall per disiplin. Brukes for å skille faglig vurdering fra automatisk matchprosent.",
          docKey: "colorfilters/beslutning",
        },

        colorSpecs: VALIDATION_DECISION_COLOR_SPECS,
        trendKeyPath: `validationDecisions.${decisionKey}`,
        getPolygonColors: makeValidationDecisionColorFn(decisionKey),
      };
    });
  }
);
